import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Modal, Form, Input, Select, DatePicker, Tabs, Popconfirm, message } from 'antd';
import { ReloadOutlined, ShoppingCartOutlined, ToolOutlined, ExportOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { storeProductApi } from '../../services/api';
import dayjs from 'dayjs';

const maintenanceTypes = { 1: '日常保养', 2: '深度清洁', 3: '维修', 4: '其他' };
const returnReasons = { 1: '退货', 2: '调货', 3: '其他' };
const transferTypeMap = { 1: { text: '退库', color: 'orange' }, 2: { text: '调货', color: 'blue' }, 3: { text: '其他', color: 'default' }, 4: { text: '工厂分配', color: 'purple' } };
const returnStatusMap = { 0: { text: '待审核', color: 'blue' }, 1: { text: '已通过', color: 'green' }, 2: { text: '已拒绝', color: 'red' } };

const Products = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [typeFilter, setTypeFilter] = useState('all');
  const [keyword, setKeyword] = useState('');

  // 销售弹框
  const [sellModalVisible, setSellModalVisible] = useState(false);
  const [sellLoading, setSellLoading] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [sellForm] = Form.useForm();

  // 保养弹框
  const [maintenanceModalVisible, setMaintenanceModalVisible] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceForm] = Form.useForm();
  const [maintenanceList, setMaintenanceList] = useState([]);
  const [editingMaintenanceId, setEditingMaintenanceId] = useState(null);

  // 退库弹框
  const [returnModalVisible, setReturnModalVisible] = useState(false);
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnForm] = Form.useForm();
  const [stores, setStores] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // 调货记录
  const [returnsLoading, setReturnsLoading] = useState(false);
  const [returnsData, setReturnsData] = useState([]);
  const [returnsPagination, setReturnsPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [returnsStatusFilter, setReturnsStatusFilter] = useState('');
  const [activeTab, setActiveTab] = useState('products');

  // 调货单详情
  const [returnDetailVisible, setReturnDetailVisible] = useState(false);
  const [returnDetailLoading, setReturnDetailLoading] = useState(false);
  const [returnDetailData, setReturnDetailData] = useState(null);

  const currentStoreId = JSON.parse(localStorage.getItem('store_admin') || '{}').store_id;

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.pageSize, type: typeFilter, keyword };
      const res = await storeProductApi.getAll(params);
      if (res.success) {
        setData(res.data.list);
        setPagination({ ...pagination, current: page, total: res.data.pagination.total });
      }
    } catch (error) {
      message.error('获取货品列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchReturns = async (page = 1) => {
    setReturnsLoading(true);
    try {
      const params = { page, limit: returnsPagination.pageSize };
      if (returnsStatusFilter !== '') params.status = returnsStatusFilter;
      const res = await storeProductApi.getReturns(params);
      if (res.success) {
        setReturnsData(res.data.list);
        setReturnsPagination({ ...returnsPagination, current: page, total: res.data.pagination.total });
      }
    } catch (error) {
      message.error('获取调货记录失败');
    } finally {
      setReturnsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [typeFilter]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'returns') fetchReturns(1);
  };

  const handleSearch = () => fetchData(1);

  // 打开销售弹框
  const handleSell = (record) => {
    if (record.inventory_store_id !== currentStoreId || record.inventory_status !== 1) {
      message.warning('只能销售本店在库货品');
      return;
    }
    setCurrentProduct(record);
    sellForm.resetFields();
    setSellModalVisible(true);
  };

  const handleSellSubmit = async () => {
    try {
      const values = await sellForm.validateFields();
      setSellLoading(true);
      await storeProductApi.sell(currentProduct.id, {
        customer: {
          last_name: values.last_name, first_name: values.first_name,
          gender: values.gender, birthday: values.birthday ? values.birthday.format('YYYY-MM-DD') : null,
          phone: values.phone, email: values.email
        },
        sale_price: values.sale_price,
        sale_date: values.sale_date ? values.sale_date.format('YYYY-MM-DD') : new Date().toISOString().split('T')[0],
        sales_person: values.sales_person, remark: values.remark
      });
      message.success('销售成功');
      setSellModalVisible(false);
      fetchData(pagination.current);
    } catch (error) {
      message.error(error.response?.data?.message || '销售失败');
    } finally {
      setSellLoading(false);
    }
  };

  // 打开保养弹框
  const handleMaintenance = async (record) => {
    if (record.inventory_store_id !== currentStoreId && record.inventory_status !== 2) {
      message.warning('只能对本店货品或已售出货品进行保养');
      return;
    }
    setCurrentProduct(record);
    setEditingMaintenanceId(null);
    maintenanceForm.resetFields();
    maintenanceForm.setFieldsValue({ maintenance_date: dayjs() });
    // 加载保养记录列表
    try {
      const res = await storeProductApi.getMaintenance(record.id);
      if (res.success) setMaintenanceList(res.data || []);
    } catch { setMaintenanceList([]); }
    setMaintenanceModalVisible(true);
  };

  // 提交保养记录（新增或编辑）
  const handleMaintenanceSubmit = async () => {
    try {
      const values = await maintenanceForm.validateFields();
      setMaintenanceLoading(true);
      const data = {
        maintenance_date: values.maintenance_date ? values.maintenance_date.format('YYYY-MM-DD') : new Date().toISOString().split('T')[0],
        maintenance_type: values.maintenance_type,
        description: values.description,
        operator: values.operator
      };
      if (editingMaintenanceId) {
        await storeProductApi.updateMaintenance(currentProduct.id, editingMaintenanceId, data);
        message.success('保养记录更新成功');
      } else {
        await storeProductApi.addMaintenance(currentProduct.id, data);
        message.success('保养记录添加成功');
      }
      // 刷新保养记录列表
      const res = await storeProductApi.getMaintenance(currentProduct.id);
      if (res.success) setMaintenanceList(res.data || []);
      setEditingMaintenanceId(null);
      maintenanceForm.resetFields();
      maintenanceForm.setFieldsValue({ maintenance_date: dayjs() });
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  // 编辑保养记录
  const handleEditMaintenance = (record) => {
    setEditingMaintenanceId(record.id);
    maintenanceForm.setFieldsValue({
      maintenance_date: record.maintenance_date ? dayjs(record.maintenance_date) : dayjs(),
      maintenance_type: record.maintenance_type,
      description: record.description,
      operator: record.operator
    });
  };

  // 删除保养记录
  const handleDeleteMaintenance = async (record) => {
    try {
      await storeProductApi.deleteMaintenance(currentProduct.id, record.id);
      message.success('保养记录删除成功');
      const res = await storeProductApi.getMaintenance(currentProduct.id);
      if (res.success) setMaintenanceList(res.data || []);
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  // 批量退库
  const handleReturn = async () => {
    const validRows = selectedRows.filter(r => r.inventory_store_id === currentStoreId && r.inventory_status === 1);
    if (validRows.length === 0) {
      message.warning('请选择本店在库的货品进行退库');
      return;
    }
    returnForm.resetFields();
    try {
      const res = await storeProductApi.getStores();
      if (res.success) setStores(res.data);
    } catch { message.error('获取门店列表失败'); return; }
    setReturnModalVisible(true);
  };

  const handleReturnSubmit = async () => {
    try {
      const values = await returnForm.validateFields();
      setReturnLoading(true);
      const validProductIds = selectedRows
        .filter(r => r.inventory_store_id === currentStoreId && r.inventory_status === 1)
        .map(r => r.id);
      await storeProductApi.returnProducts({
        product_ids: validProductIds,
        to_store_id: values.to_store_id,
        return_reason: values.return_reason,
        remark: values.remark
      });
      message.success('退库申请已提交，等待管理员审核');
      setReturnModalVisible(false);
      setSelectedRows([]);
      fetchData(pagination.current);
    } catch (error) {
      message.error(error.response?.data?.message || '退库申请失败');
    } finally {
      setReturnLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedRows.map(r => r.id),
    onChange: (_, rows) => setSelectedRows(rows),
    getCheckboxProps: (record) => ({
      disabled: record.inventory_store_id !== currentStoreId || record.inventory_status !== 1
    })
  };

  const getStatusTag = (status, storeId) => {
    if (status === 3) return <Tag color="orange">退库中</Tag>;
    if (status === 2) return <Tag color="red">已售出</Tag>;
    if (storeId === currentStoreId && status === 1) return <Tag color="green">本店在库</Tag>;
    return <Tag color="orange">在库</Tag>;
  };

  const columns = [
    { title: '货品编码', dataIndex: 'product_code', width: 120 },
    { title: '货品名称', dataIndex: 'name' },
    { title: '品类', dataIndex: 'category' },
    { title: '材质', dataIndex: 'material' },
    { title: '重量(g)', dataIndex: 'weight' },
    {
      title: '状态',
      dataIndex: 'inventory_status',
      render: (status, record) => getStatusTag(status, record.inventory_store_id)
    },
    {
      title: '所在门店',
      dataIndex: 'store_name',
      render: (name, record) => name === '工厂' ? '工厂' : (name || '-')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          {record.inventory_store_id === currentStoreId && record.inventory_status === 1 && (
            <Button type="link" icon={<ShoppingCartOutlined />} onClick={() => handleSell(record)}>销售</Button>
          )}
          {(record.inventory_store_id === currentStoreId || record.inventory_status === 2) && (
            <Button type="link" icon={<ToolOutlined />} onClick={() => handleMaintenance(record)}>保养</Button>
          )}
        </Space>
      )
    }
  ];

  // 调货记录表格列（整单）
  const returnsColumns = [
    { title: '调货单号', dataIndex: 'order_no', width: 150 },
    {
      title: '调货类型', dataIndex: 'transfer_type', width: 90,
      render: (v) => {
        const t = transferTypeMap[v] || { text: '未知', color: 'default' };
        return <Tag color={t.color}>{t.text}</Tag>;
      }
    },
    { title: '来源门店', dataIndex: 'from_store_name', render: (v) => v === '工厂' ? '工厂' : (v || '-') },
    { title: '目标门店', dataIndex: 'to_store_name' },
    {
      title: '货品数量', dataIndex: 'product_count', width: 80,
      render: (v, record) => v || record.total_count || '-'
    },
    { title: '备注', dataIndex: 'remark', ellipsis: true },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (v) => {
        const s = returnStatusMap[v] || { text: '未知', color: 'default' };
        return <Tag color={s.color}>{s.text}</Tag>;
      }
    },
    {
      title: '时间', dataIndex: 'created_at', width: 160,
      render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-'
    },
    {
      title: '审核信息', width: 140,
      render: (_, record) => {
        if (record.status === 0) return '-';
        return (
          <div style={{ fontSize: 12 }}>
            <div>{record.reviewer_name || '-'}</div>
            {record.status === 2 && record.reject_reason && <div style={{ color: '#999' }}>原因: {record.reject_reason}</div>}
          </div>
        );
      }
    },
    {
      title: '操作', width: 80,
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => showReturnDetail(record.id)}>详情</Button>
      )
    }
  ];

  const showReturnDetail = async (orderId) => {
    setReturnDetailVisible(true);
    setReturnDetailLoading(true);
    try {
      const res = await storeProductApi.getReturnDetail(orderId);
      if (res.success) setReturnDetailData(res.data);
    } catch (error) {
      message.error('获取调货单详情失败');
    } finally {
      setReturnDetailLoading(false);
    }
  };

  return (
    <div>
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={[
        {
          key: 'products',
          label: '货品列表',
          children: (
            <>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Space>
                  <Button icon={<ReloadOutlined />} onClick={() => fetchData(pagination.current)}>刷新</Button>
                  <Select value={typeFilter} style={{ width: 120 }} onChange={(value) => { setTypeFilter(value); setKeyword(''); }}>
                    <Select.Option value="all">全部货品</Select.Option>
                    <Select.Option value="store">本店货品</Select.Option>
                    <Select.Option value="sold">已售出</Select.Option>
                  </Select>
                  <Input.Search placeholder="搜索货品编号或名称" value={keyword} onChange={(e) => setKeyword(e.target.value)} onSearch={handleSearch} style={{ width: 200 }} />
                </Space>
                <Space>
                  <span style={{ marginRight: 8 }}>已选 {selectedRows.filter(r => r.inventory_store_id === currentStoreId && r.inventory_status === 1).length} 项</span>
                  <Button type="primary" icon={<ExportOutlined />} onClick={handleReturn}
                    disabled={selectedRows.filter(r => r.inventory_store_id === currentStoreId && r.inventory_status === 1).length === 0}>
                    批量退库
                  </Button>
                </Space>
              </div>
              <Table rowSelection={rowSelection} columns={columns} dataSource={data} loading={loading} rowKey="id"
                pagination={{ ...pagination, onChange: (page) => fetchData(page) }} />
            </>
          )
        },
        {
          key: 'returns',
          label: '调货记录',
          children: (
            <>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Space>
                  <Button icon={<ReloadOutlined />} onClick={() => fetchReturns(returnsPagination.current)}>刷新</Button>
                  <Select value={returnsStatusFilter} style={{ width: 120 }} onChange={(v) => { setReturnsStatusFilter(v); }} placeholder="全部状态" allowClear>
                    <Select.Option value="">全部状态</Select.Option>
                    <Select.Option value={0}>待审核</Select.Option>
                    <Select.Option value={1}>已通过</Select.Option>
                    <Select.Option value={2}>已拒绝</Select.Option>
                  </Select>
                </Space>
              </div>
              <Table columns={returnsColumns} dataSource={returnsData} loading={returnsLoading} rowKey="id"
                pagination={{ ...returnsPagination, onChange: (page) => fetchReturns(page) }} />
            </>
          )
        }
      ]} />

      {/* 销售弹框 */}
      <Modal title="销售货品" open={sellModalVisible} onCancel={() => setSellModalVisible(false)} onOk={handleSellSubmit} confirmLoading={sellLoading} width={600}>
        <div style={{ marginBottom: 16 }}><strong>货品信息：</strong>{currentProduct?.product_code} - {currentProduct?.name}</div>
        <Form form={sellForm} layout="vertical">
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="last_name" label="姓" rules={[{ required: true }]} style={{ flex: 1 }}><Input placeholder="请输入姓" /></Form.Item>
            <Form.Item name="first_name" label="名" rules={[{ required: true }]} style={{ flex: 1 }}><Input placeholder="请输入名" /></Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="gender" label="性别" style={{ flex: 1 }}>
              <Select placeholder="请选择性别"><Select.Option value={1}>男</Select.Option><Select.Option value={2}>女</Select.Option></Select>
            </Form.Item>
            <Form.Item name="birthday" label="出生日期" style={{ flex: 1 }}><DatePicker style={{ width: '100%' }} /></Form.Item>
          </div>
          <Form.Item name="phone" label="手机号" rules={[{ required: true }]}><Input placeholder="请输入手机号" /></Form.Item>
          <Form.Item name="email" label="邮箱"><Input placeholder="请输入邮箱" /></Form.Item>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="sale_price" label="销售价格" style={{ flex: 1 }}><Input type="number" placeholder="请输入销售价格" addonAfter="元" /></Form.Item>
            <Form.Item name="sale_date" label="销售日期" style={{ flex: 1 }}><DatePicker style={{ width: '100%' }} /></Form.Item>
          </div>
          <Form.Item name="sales_person" label="销售人员"><Input placeholder="请输入销售人员" /></Form.Item>
          <Form.Item name="remark" label="备注"><Input.TextArea rows={2} placeholder="请输入备注" /></Form.Item>
        </Form>
      </Modal>

      {/* 保养记录弹框 */}
      <Modal
        title={editingMaintenanceId ? '编辑保养记录' : '保养记录'}
        open={maintenanceModalVisible}
        onCancel={() => { setMaintenanceModalVisible(false); setEditingMaintenanceId(null); }}
        onOk={handleMaintenanceSubmit}
        confirmLoading={maintenanceLoading}
        width={700}
        okText={editingMaintenanceId ? '保存修改' : '添加记录'}
      >
        <div style={{ marginBottom: 16 }}><strong>货品信息：</strong>{currentProduct?.product_code} - {currentProduct?.name}</div>

        {/* 保养记录列表 */}
        {maintenanceList.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ marginBottom: 8 }}>历史保养记录</h4>
            <Table
              dataSource={maintenanceList}
              rowKey="id"
              size="small"
              pagination={false}
              columns={[
                { title: '日期', dataIndex: 'maintenance_date', width: 100 },
                { title: '类型', dataIndex: 'maintenance_type', width: 80, render: (v) => maintenanceTypes[v] || '-' },
                { title: '门店', dataIndex: 'store_name', width: 100 },
                { title: '操作人', dataIndex: 'operator', width: 80 },
                { title: '描述', dataIndex: 'description', ellipsis: true },
                {
                  title: '操作', width: 100,
                  render: (_, record) => (
                    record.store_id === currentStoreId ? (
                      <Space size="small">
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditMaintenance(record)} />
                        <Popconfirm title="确定删除该保养记录？" onConfirm={() => handleDeleteMaintenance(record)}>
                          <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    ) : null
                  )
                }
              ]}
            />
          </div>
        )}

        {/* 新增/编辑表单 */}
        <h4 style={{ marginBottom: 8 }}>{editingMaintenanceId ? '编辑记录' : '新增保养记录'}</h4>
        <Form form={maintenanceForm} layout="vertical">
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="maintenance_date" label="保养日期" initialValue={dayjs()} style={{ flex: 1 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="maintenance_type" label="保养类型" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Select placeholder="请选择保养类型">
                {Object.entries(maintenanceTypes).map(([k, v]) => <Select.Option key={k} value={Number(k)}>{v}</Select.Option>)}
              </Select>
            </Form.Item>
          </div>
          <Form.Item name="operator" label="操作人"><Input placeholder="请输入操作人" /></Form.Item>
          <Form.Item name="description" label="保养描述"><Input.TextArea rows={2} placeholder="请输入保养描述" /></Form.Item>
        </Form>
        {editingMaintenanceId && (
          <Button style={{ marginTop: -8 }} onClick={() => { setEditingMaintenanceId(null); maintenanceForm.resetFields(); maintenanceForm.setFieldsValue({ maintenance_date: dayjs() }); }}>
            取消编辑
          </Button>
        )}
      </Modal>

      {/* 批量退库弹框 */}
      <Modal title="批量退库" open={returnModalVisible} onCancel={() => setReturnModalVisible(false)} onOk={handleReturnSubmit} confirmLoading={returnLoading}>
        <div style={{ marginBottom: 16 }}>
          <strong>已选货品：</strong>
          {selectedRows.filter(r => r.inventory_store_id === currentStoreId && r.inventory_status === 1).map(r => r.product_code).join(', ')}
        </div>
        <Form form={returnForm} layout="vertical">
          <Form.Item name="to_store_id" label="退库至门店" rules={[{ required: true, message: '请选择目标门店' }]}>
            <Select placeholder="请选择目标门店">
              {stores.map(store => <Select.Option key={store.id} value={store.id}>{store.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="return_reason" label="退库原因" rules={[{ required: true }]}>
            <Select placeholder="请选择退库原因">
              {Object.entries(returnReasons).map(([k, v]) => <Select.Option key={k} value={Number(k)}>{v}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="remark" label="备注"><Input.TextArea rows={2} placeholder="请输入备注" /></Form.Item>
        </Form>
      </Modal>

      {/* 调货单详情弹框 */}
      <Modal title={`调货单详情 ${returnDetailData?.order?.order_no || ''}`} open={returnDetailVisible}
        onCancel={() => setReturnDetailVisible(false)} footer={null} width={600}>
        {returnDetailLoading ? (
          <p>加载中...</p>
        ) : returnDetailData ? (
          <>
            <div style={{ marginBottom: 12 }}>
              <Space size={16}>
                <span>来源: {returnDetailData.order.from_store_name === '工厂' ? '工厂' : (returnDetailData.order.from_store_name || '-')}</span>
                <span>目标: {returnDetailData.order.to_store_name}</span>
                <span>类型: {(() => { const t = transferTypeMap[returnDetailData.order.transfer_type] || {}; return <Tag color={t.color}>{t.text}</Tag>; })()}</span>
                <span>数量: {returnDetailData.order.total_count}</span>
                {(() => { const s = returnStatusMap[returnDetailData.order.status] || {}; return <Tag color={s.color}>{s.text}</Tag>; })()}
              </Space>
            </div>
            {returnDetailData.order.remark && <div style={{ marginBottom: 12, color: '#666' }}>备注: {returnDetailData.order.remark}</div>}
            <Table
              columns={[
                { title: '货品编码', dataIndex: 'product_code', width: 120 },
                { title: '货品名称', dataIndex: 'product_name' },
                { title: '品类', dataIndex: 'category', width: 80 },
                { title: '材质', dataIndex: 'material', width: 80 },
              ]}
              dataSource={returnDetailData.items}
              rowKey="id"
              size="small"
              pagination={false}
            />
          </>
        ) : null}
      </Modal>
    </div>
  );
};

export default Products;
